import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, GitCompare, Globe, Lock, Bookmark, AlertCircle } from 'lucide-react';

interface SaveComparisonGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, description: string, isPublic: boolean) => Promise<void>;
  products: any[];
  initialData?: {
    name: string;
    description?: string;
    isPublic: boolean;
  };
  isEditing?: boolean;
}

const SaveComparisonGroupModal: React.FC<SaveComparisonGroupModalProps> = ({
  isOpen,
  onClose,
  onSave,
  products,
  initialData,
  isEditing = false
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resetear form cuando se abre/cierra el modal
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setName(initialData.name);
        setDescription(initialData.description || '');
        setIsPublic(initialData.isPublic);
      } else {
        setName('');
        setDescription('');
        setIsPublic(true);
      }
      setError(null);
    }
  }, [isOpen, initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t('comparison_groups.group_name') + ' ' + t('comparison_groups.name_required'));
      return;
    }

    if (products.length === 0) {
      setError(t('comparison_groups.no_products_to_save'));
      return;
    }

    setIsSaving(true);

    try {
      await onSave(name.trim(), description.trim(), isPublic);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('comparison_groups.error_save'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <GitCompare className="h-5 w-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {isEditing 
                ? t('comparison_groups.edit_group') 
                : t('comparison_groups.save_group')
              }
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {products.length}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Nombre del grupo */}
          <div>
            <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 mb-1">
              {t('comparison_groups.group_name')} *
            </label>
            <input
              type="text"
              id="groupName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder={t('comparison_groups.group_name_placeholder')}
              maxLength={100}
              required
            />
          </div>

          {/* Descripción */}
          <div>
            <label htmlFor="groupDescription" className="block text-sm font-medium text-gray-700 mb-1">
              {t('comparison_groups.description')}
            </label>
            <textarea
              id="groupDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
              placeholder={t('comparison_groups.description_placeholder')}
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Visibilidad */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('comparison_groups.visibility')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-start space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="visibility"
                  checked={isPublic}
                  onChange={() => setIsPublic(true)}
                  className="text-purple-600 focus:ring-purple-500 mt-0.5"
                />
                <div className="flex items-start space-x-2 min-w-0">
                  <Globe className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-900 block">
                      {t('comparison_groups.public')}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('comparison_groups.public_description')}
                    </p>
                  </div>
                </div>
              </label>
              
              <label className="flex items-start space-x-3 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="visibility"
                  checked={!isPublic}
                  onChange={() => setIsPublic(false)}
                  className="text-purple-600 focus:ring-purple-500 mt-0.5"
                />
                <div className="flex items-start space-x-2 min-w-0">
                  <Lock className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-900 block">
                      {t('comparison_groups.private')}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('comparison_groups.private_description')}
                    </p>
                  </div>
                </div>
              </label>
            </div>
          </div>

        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || !name.trim()}
            className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Bookmark className="h-4 w-4" />
            <span>{isSaving ? t('common.saving') : t('common.save')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveComparisonGroupModal; 